import React from "react";

import { SkillPanelLogo } from "@/assets/images";
import hrpmLogo from "@/assets/images/logos/hrpm-logo.svg";
import { TrustBadges } from "@/components/ui/TrustBadges";
import { useDocumentTitle } from "@/hooks/useDocumentTitle";

/** The display headline beside the form: a lead, its key phrase in violet, and supporting copy. */
export interface AuthHeadline {
  lead: string;
  highlight: string;
  description: string;
}

const WELCOME: AuthHeadline = {
  lead: "Welcome back to your",
  highlight: "skills platform",
  description:
    "Access your personalized dashboard, track your progress, and continue your learning journey.",
};

export const AuthLayout = (props: {
  /** Names the page: its heading for screen readers and the browser tab */
  title: string;
  /** For a screen that is not sign-in, such as a support code; sign-in screens keep the welcome */
  headline?: AuthHeadline;
  children?: React.ReactNode;
}) => {
  const { title, headline = WELCOME } = props;
  const showHrpmLogo =
    window.location.hostname.startsWith("hrpm.") || window.location.search.includes("client=hrpm");

  // The front door is the way into SkillPanel; UserManager is the admin app behind it
  useDocumentTitle(title, "SkillPanel");

  return (
    <div className="front-door min-h-screen flex flex-col lg:flex-row bg-gradient-clip">
      {/* The headline is hidden below 1024px, so the page heading is a separate, always-present one */}
      <h1 className="sr-only">{title}</h1>

      {/* Branding and Social Proof - Bottom on mobile, Left on desktop */}
      <div className="w-full lg:w-1/2 px-4 py-8 sm:p-8 lg:p-16 flex flex-col lg:justify-center order-2 lg:order-1 bg-transparent lg:bg-background">
        <div className="flex flex-col space-y-12 lg:space-y-16">
          {/* Logo - hidden on mobile */}
          <div className="hidden lg:flex items-center">
            <SkillPanelLogo height={32} />
          </div>

          {/* Main content - Hidden on mobile */}
          <div className="hidden lg:block space-y-6 lg:space-y-8 max-w-xl">
            <p className="text-3xl lg:text-5xl font-semibold leading-[1.15] text-brand">
              {headline.lead}{" "}
              {/* The key phrase takes its own line, so it never splits from the lead mid-phrase */}
              <span className="block text-primary">{headline.highlight}</span>
            </p>
            <p className="text-base lg:text-lg text-foreground/80 leading-relaxed">
              {headline.description}
            </p>
          </div>

          <div className="mt-6 lg:mt-10">{showHrpmLogo ? <HrpmLogo /> : <TrustBadges />}</div>
        </div>
      </div>

      {/* Login Form - Top on mobile, Right on desktop */}
      <div className="flex-1 flex items-center justify-center px-4 py-8 sm:p-8 order-1 lg:order-2 bg-transparent lg:bg-gradient-clip">
        <div className="w-full max-w-md space-y-8">
          {/* Mobile logo */}
          <div className="lg:hidden flex items-center justify-center mb-8">
            <SkillPanelLogo className="h-7" />
          </div>

          {props.children}
        </div>
      </div>
    </div>
  );
};

const HrpmLogo = () => {
  return (
    <div className="w-75 max-w-full">
      <img src={hrpmLogo} alt="HR Power Mentor" className="w-full" />
    </div>
  );
};
